/**
 * CanvasCardEditor — single-mount CollabEditor positioned as a popover
 * over the currently-edited card. Option-4 hybrid pattern per Content
 * IACP `c625d7f9`.
 *
 * Architecture:
 *   - CollabEditor is mounted ONCE per CanvasView lifetime. Yjs sync
 *     + WebsocketProvider stay alive across edit-open/close cycles —
 *     no remount cost, no extra WS connections.
 *   - When `editingBlockId` is null: container is `visibility: hidden`
 *     + `pointer-events: none`. Editor still receives Yjs updates.
 *   - When set: container becomes visible, positioned over the
 *     target card (anchorRect prop), and a scoped inline `<style>`
 *     hides every paragraph except the one with the matching
 *     `data-block-id`. Auto-focus + selection set on the target.
 *   - Cmd+Enter / Esc / blur outside → close popover (parent clears
 *     editingBlockId).
 *
 * Single Y.Doc, single WS, all CollabEditor plugins (PendingDraft
 * decoration "lightgrey-bis-Enter", AnnotationHighlight,
 * AuthorMark V2.3 re-stamp, FieldSalienceDecoration tension cues,
 * chunk_committed triggers via newline_pause / blur / agent_write)
 * apply for free. Concurrent-edit-safe via CRDT.
 */

import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { CollabEditor, colorFromId } from './CollabEditor'
import { getAccessToken, getUser } from '../auth'

interface Props {
  postId: number
  editingBlockId: string | null
  /** Paragraph index of the editing target. Used as fallback selector
   *  when block_id is null (pre-V2.8.0 paragraphs not yet lazy-stamped).
   *  `:nth-child(N+1)` on the ProseMirror surface matches by document
   *  order. */
  editingParagraphIndex: number | null
  /** Bounding rect of the target card on screen, used to position the
   *  popover over it. Updated by parent when card moves or canvas
   *  pans/zooms while edit is open. Null → use default centered. */
  anchorRect: DOMRect | null
  /** Author-name list for @-mention autocomplete (same shape as
   *  CollabEditor's mentionItems). */
  mentionItems?: string[]
  /** Called when the user presses Cmd+Enter / Esc / blurs outside. */
  onRequestClose: () => void
  /** Obergrenze der Popover-Breite (Canvas-Karten 560, Lesemodus breiter). */
  maxWidth?: number
  /** Selektor des Quell-Elements (Karte bzw. Lesemodus-Absatz) — ein
   *  Klick darauf schließt den Popover nicht. Default `.canvas-card`. */
  sourceSelector?: string
  /** `read`: Lesemodus-Absatz — Popover sitzt deckungsgleich auf dem
   *  Absatz (kein Karten-Rahmen, gleiche Schrift), damit es wie
   *  Bearbeiten an Ort und Stelle wirkt. */
  variant?: 'card' | 'read'
}

export function CanvasCardEditor({
  postId,
  editingBlockId,
  editingParagraphIndex,
  anchorRect,
  mentionItems,
  onRequestClose,
  maxWidth = 560,
  sourceSelector = '.canvas-card',
  variant = 'card',
}: Props) {
  const [editor, setEditor] = useState<Editor | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onRequestClose)
  onCloseRef.current = onRequestClose

  // User identity for CollabEditor — same derivation as PostEditor.
  const authUser = getUser()
  const token = getAccessToken()
  const user = {
    id: authUser ? `user:${authUser.id}` : 'anon',
    name: authUser
      ? `${authUser.first_name} ${authUser.last_name}`.trim() || authUser.email
      : 'Anon',
    color: colorFromId(authUser?.id || 'anon'),
  }

  // Focus the target paragraph when editingBlockId changes. Beim
  // Lazy-Mount (Lesemodus) ist der Editor beim ersten Versuch noch leer —
  // der Yjs-Sync kommt erst nach dem WS-Handshake. Darum bis zu ~5 s
  // nachfassen, statt beim ersten Fehlschlag aufzugeben.
  useEffect(() => {
    if (!editor || !editingBlockId) return
    let tries = 0
    let timer: number | null = null
    const attempt = () => {
      const pos = findParagraphPosByBlockId(editor, editingBlockId)
      if (pos == null) {
        if (++tries < 34) timer = window.setTimeout(attempt, 150)
        return
      }
      editor.commands.focus()
      editor.commands.setTextSelection({ from: pos, to: pos })
      // Ein unsichtbarer Container (Rect noch nicht gemessen) nimmt
      // keinen Fokus an — dann nachfassen, bis er wirklich sitzt.
      if (!editor.isFocused && ++tries < 34) timer = window.setTimeout(attempt, 150)
    }
    attempt()
    return () => {
      if (timer != null) window.clearTimeout(timer)
    }
  }, [editor, editingBlockId])

  // Keyboard handling: Cmd/Ctrl+Enter = explicit commit + close,
  // Esc = close without commit (the PendingDraftDecoration keeps the
  // grey state visible if there's an uncommitted burst; CollabEditor's
  // own blur trigger will fire a chunk_committed on next focus loss
  // anyway). Plain Enter does the normal new-paragraph behavior —
  // Yjs syncs and the new paragraph appears as a new card.
  useEffect(() => {
    if (!editor || !editingBlockId) return
    const dom = (editor.view as { dom?: HTMLElement }).dom
    if (!dom) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
        return
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        onCloseRef.current()
        return
      }
    }
    dom.addEventListener('keydown', handler)
    return () => dom.removeEventListener('keydown', handler)
  }, [editor, editingBlockId])

  // Click-outside close. Mousedown handler on document captures
  // clicks that don't hit the popover container OR the source card.
  useEffect(() => {
    if (!editingBlockId) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!target) return
      // Click inside popover → ignore.
      if (containerRef.current?.contains(target)) return
      // Click on the source card/paragraph → also ignore (user might
      // want to grab the resize handle or click "Read" to close).
      const sources = document.querySelectorAll(
        `${sourceSelector}[data-block-id="${cssEscape(editingBlockId)}"]`,
      )
      for (const el of Array.from(sources)) {
        if (el.contains(target)) return
      }
      onCloseRef.current()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [editingBlockId, sourceSelector])

  const visible = editingBlockId != null && anchorRect != null
  // Popover sizing: hug the source card's width (with a sensible
  // minimum for legibility), height auto-derives from content. Sits
  // directly on top of the source card so visually the card "becomes"
  // editable in place rather than spawning a separate panel.
  const containerStyle: React.CSSProperties = visible
    ? {
        position: 'fixed',
        // Lesemodus: Rahmen (1px) + Innenabstand (2px) nach außen
        // ausgleichen, damit der Text exakt auf der gerenderten Zeile liegt.
        left: anchorRect.left - (variant === 'read' ? 3 : 0),
        top: anchorRect.top - (variant === 'read' ? 3 : 0),
        width: Math.max(280, Math.min(maxWidth, anchorRect.width)) + (variant === 'read' ? 6 : 0),
        visibility: 'visible',
        pointerEvents: 'auto',
        zIndex: 50,
      }
    : {
        position: 'fixed',
        left: 0,
        top: 0,
        width: 1,
        height: 1,
        visibility: 'hidden',
        pointerEvents: 'none',
        zIndex: -1,
      }

  return (
    <div
      ref={containerRef}
      className={`canvas-card-popover canvas-card-popover--${variant}`}
      style={containerStyle}
    >
      {/* Inline scope: hide CollabEditor chrome + every paragraph
        * except the editing target. Per Content's CSS-scope plan +
        * additional chrome-hide for popover context (status-bar +
        * authors-legend belong to doc-mode, not card-edit). */}
      {visible && editingBlockId && (
        <style>{`
          /* Positive-selection chrome hide: hide everything inside
           * .collab-editor EXCEPT the editor surface (where the
           * ProseMirror element lives). This catches authors-legend,
           * status bar, peers list, mention dropdown chrome — anything
           * the CollabEditor renders outside the surface itself. */
          .canvas-card-popover .collab-editor > *:not(.collab-editor-surface) {
            display: none !important;
          }
          /* Hide every paragraph + heading except the editing target.
           * Block_id (UUID) match preferred; nth-child fallback for
           * pre-V2.8.0 paragraphs with no server-stamped block_id. */
          .canvas-card-popover .ProseMirror > * {
            display: none !important;
          }
          ${
            !editingBlockId.startsWith('_paragraph_')
              ? `.canvas-card-popover .ProseMirror > [data-block-id="${cssEscape(editingBlockId)}"] { display: block !important; }`
              : ''
          }
          ${
            editingParagraphIndex != null
              ? `.canvas-card-popover .ProseMirror > *:nth-child(${editingParagraphIndex + 1}) { display: block !important; }`
              : ''
          }
          /* Override the doc-mode CSS min-heights that turn the popover
           * into a 600+px tall slab. Global.css sets
           * min-height calc(100vh - 280/320px) on .collab-editor,
           * .collab-editor-surface, AND .ProseMirror for doc-mode use.
           * In popover we want content-driven sizing. */
          .canvas-card-popover .collab-editor {
            display: flex;
            flex-direction: column;
            gap: 0;
            min-height: 0 !important;
          }
          .canvas-card-popover .collab-editor-surface {
            flex: none !important;
            min-height: 0 !important;
            background: var(--bg, #fff);
            border: 2px solid var(--accent, #aa3bff);
            border-radius: 8px;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
          }
          .canvas-card-popover .collab-editor-surface .ProseMirror {
            padding: 10px 12px;
            min-height: 0 !important;
            max-height: 50vh;
            overflow-y: auto;
            background: transparent;
            border: none;
            outline: none;
            font-size: 0.95em;
            line-height: 1.4;
          }
          .canvas-card-popover .collab-editor-surface .ProseMirror p {
            margin: 0;
          }
        `}</style>
      )}
      <CollabEditor
        postId={postId}
        token={token}
        user={user}
        readOnly={false}
        placeholder=""
        mentionItems={mentionItems}
        onEditorReady={setEditor}
      />
    </div>
  )
}

/**
 * Walk the ProseMirror doc looking for the first paragraph node whose
 * `block_id` attribute matches. Returns the position of the start of
 * the paragraph's text content, or null if not found.
 */
function findParagraphPosByBlockId(editor: Editor, blockId: string): number | null {
  let result: number | null = null
  editor.state.doc.descendants((node, pos) => {
    if (result !== null) return false
    if (node.type.name !== 'paragraph') return true
    const attrs = node.attrs as { block_id?: string | null }
    if (attrs.block_id === blockId) {
      // +1 to land inside the paragraph (after the open-token).
      result = pos + 1
      return false
    }
    return true
  })
  return result
}

/**
 * CSS.escape polyfill — block_id is a UUID so usually safe, but
 * defensive for fallback `_paragraph_N` keys with the underscore +
 * digit shape (no metacharacters but still cheap to be safe).
 */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}
